import { useState, useEffect, useRef } from "react";
import {
  Modal,
  Box,
  TextField,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  Grid,
  Typography,
  Tooltip,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Picker from "emoji-picker-react";

// Components
import { useNotification } from "../utils/Notification";
import ConfirmationModal from "../utils/ConfirmationModal";
import { trackEvent } from "../utils/Utils";

// Context
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

// Supabase
import supabase from "../../supabaseClient";

// i18n
import { useTranslation } from "react-i18next";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "80%", md: "60%", lg: "50%" },
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "24px",
  boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  overflowY: "auto",
  p: { xs: 3, md: 5 },
};

export default function Template({
  open,
  onClose,
  isEditing,
  template,
  onSubmit,
}) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    content: "",
    file_id: null,
  });
  const [files, setFiles] = useState([]);
  const [contactVariables, setContactVariables] = useState([
    "first_name",
    "last_name",
    "nickname",
    "birthday",
    "countrycode_id",
    "phone",
  ]);
  const [profileVariables, setProfileVariables] = useState([
    "first_name",
    "last_name",
    "nickname",
    "countrycode_id",
    "phone",
  ]);
  const koubiVariables = ["day", "month", "year", "date"];
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Context
  const { teamId } = useAuth();
  const { showNotification } = useNotification();

  // Menu State Variables
  const [showVariableMenu, setShowVariableMenu] = useState(false);
  const [showContactVariables, setShowContactVariables] = useState(false);
  const [showProfileVariables, setShowProfileVariables] = useState(false);
  const [showKoubiVariables, setShowKoubiVariables] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const contentRef = useRef(null);
  const navigate = useNavigate();
  const [tooltipOpen, setTooltipOpen] = useState(false);

  useEffect(() => {
    const fetchFiles = async () => {
      const { data, error } = await supabase
        .from("files")
        .select("id, name")
        .eq("role_access", "{}");

      if (error) {
        showNotification(
          t("template.errorFetchingFiles", { error: error.message }),
          "error"
        );
      } else {
        setFiles(data);
      }
    };

    const fetchContactVariables = async () => {
      const { data, error } = await supabase.from("variables").select("key");

      if (error) {
        showNotification(
          t("template.errorFetchingContactVariables", { error: error.message }),
          "error"
        );
      } else {
        const variableKeys = data ? data.map((item) => item.key) : [];
        const allVariableKeys = [
          ...contactVariables, // Default variables
          ...variableKeys, // Fetched variables
        ];
        setContactVariables(allVariableKeys);
      }
    };

    const fetchProfileVariables = async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("variables");

      if (error) {
        showNotification(
          t("template.errorFetchingProfileVariables", { error: error.message }),
          "error"
        );
      } else {
        const variableKeys =
          data && data[0] && data[0].variables
            ? Object.keys(data[0].variables)
            : [];
        const allVariableKeys = [
          ...profileVariables, // Default variables
          ...variableKeys, // Fetched variables
        ];
        setProfileVariables(allVariableKeys);
      }
    };

    fetchFiles();
    fetchContactVariables();
    fetchProfileVariables();
  }, []);

  useEffect(() => {
    if (isEditing && template) {
      setFormData({
        name: template.name,
        content: template.content,
        file_id: template.file_id?.id || "",
      });
    }
  }, [isEditing, template, files]);

  const handleChange = (field, value) => {
    setShowEmojiPicker(false);
    setFormData((prevData) => ({ ...prevData, [field]: value || null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    trackEvent("template-form_submit");

    try {
      if (isEditing) {
        const { error } = await supabase
          .from("templates")
          .update({
            ...formData,
            file_id: formData.file_id || null,
          })
          .eq("id", template.id);

        if (error) {
          throw error;
        }
      } else {
        const { error } = await supabase.from("templates").insert([
          {
            ...formData,
            team_id: teamId,
          },
        ]);

        if (error) {
          throw error;
        }
      }

      onSubmit();
      resetValues();
    } catch (error) {
      showNotification(
        t("template.errorTemplateAction", {
          action: isEditing ? "updating" : "adding",
          error: error.message,
        }),
        "error"
      );
    }
  };

  const handleDelete = () => {
    trackEvent("template-delete_clicked");
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirmation = async () => {
    trackEvent("template-delete_confirmed");
    try {
      if (template) {
        const { error } = await supabase
          .from("templates")
          .delete()
          .eq("id", template.id);

        if (error) {
          throw error;
        }

        onSubmit();
        resetValues();
      }
    } catch (error) {
      showNotification(
        t("template.errorDeletingTemplate", { error: error.message }),
        "error"
      );
    }
  };

  const handleDeleteCancellation = () => {
    trackEvent("template-delete_cancelled");
    setShowDeleteConfirmation(false);
  };

  const resetValues = () => {
    setShowDeleteConfirmation(false);
    setFormData({
      name: "",
      content: "",
      file_id: null,
    });
    setShowContactVariables(false);
    setShowProfileVariables(false);
    setShowKoubiVariables(false);
    setShowVariableMenu(false);
    setShowEmojiPicker(false);
    onClose();
  };

  // Handle variables menu:
  const handleContentChange = (e) => {
    const content = e.target.value;
    const cursorPosition = e.target.selectionStart;

    handleChange("content", content);

    if (content[cursorPosition - 1] === "@") {
      trackEvent("template-show_variables_menu_opened");
      const rect = contentRef.current.getBoundingClientRect();
      setMenuPosition({ x: rect.left, y: rect.bottom });
      setShowVariableMenu(true);
    } else {
      setShowVariableMenu(false);
      setShowContactVariables(false);
      setShowProfileVariables(false);
      setShowKoubiVariables(false);
    }
  };

  const handleVariableSelect = (variable, type) => {
    trackEvent("template-variable_select", { variable, type });
    const currentContent = formData.content;
    const cursorPosition = contentRef.current.selectionStart;
    const newContent = `${currentContent.substring(
      0,
      cursorPosition - 1
    )}%${type}.${variable}%${currentContent.substring(cursorPosition)}`;
    handleChange("content", newContent);
    setShowContactVariables(false);
    setShowProfileVariables(false);
    setShowKoubiVariables(false);
    setShowVariableMenu(false);
  };

  const handleNewFileClick = () => {
    trackEvent("template-new_file_clicked");
    navigate("/dashboard/files");
  };

  const handleEmojiClick = (event) => {
    const cursorPosition = contentRef.current.selectionStart;
    const currentContent = formData.content;
    const newContent =
      currentContent.substring(0, cursorPosition) +
      event.emoji +
      currentContent.substring(cursorPosition);
    handleChange("content", newContent);
  };

  return (
    <Modal open={open} onClose={resetValues}>
      <Box sx={style}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Form fields */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("template.name")}
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">
                {t("message.variablesTutorial")}
                <a
                  href={t("message.variablesTutorialUrl")}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("message.tutorial")}
                </a>
                .{/* Tooltip Icon next to the text */}
                <Tooltip
                  title={
                    <h2 style={{ fontSize: "13px" }}>
                      {t("template.variablesDescription")}
                    </h2>
                  }
                  open={tooltipOpen}
                  onClose={() => setTooltipOpen(false)}
                  disableHoverListener
                >
                  <IconButton
                    size="small"
                    sx={{ ml: 1 }}
                    onClick={() => setTooltipOpen(true)}
                  >
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ position: "relative" }}>
                <TextField
                  fullWidth
                  label={t("template.messageText")}
                  inputRef={contentRef}
                  onChange={handleContentChange}
                  required
                  value={formData.content}
                  multiline
                  rows={4}
                  InputProps={{
                    // Padding from text inside TextField
                    sx: { paddingRight: "28px" },
                  }}
                />
                <IconButton
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  sx={{
                    position: "absolute",
                    top: -2,
                    right: -1,
                    color: "black",
                  }}
                >
                  <span
                    role="img"
                    aria-label="emoji"
                    style={{ fontSize: "1rem" }}
                  >
                    😊
                  </span>
                </IconButton>
                {showEmojiPicker && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 40,
                      right: 0,
                      zIndex: 1000,
                      border: "1px solid #ddd",
                      backgroundColor: "white",
                    }}
                  >
                    <Picker onEmojiClick={handleEmojiClick} />
                  </Box>
                )}
              </Box>
              <Menu
                open={showVariableMenu}
                onClose={() => setShowVariableMenu(false)}
                anchorReference="anchorPosition"
                anchorPosition={{ top: menuPosition.y, left: menuPosition.x }}
              >
                <MenuItem onClick={() => setShowContactVariables(true)}>
                  {t("message.contact")}
                  <Menu
                    open={showContactVariables}
                    onClose={() => setShowContactVariables(false)}
                    anchorReference="anchorPosition"
                    anchorPosition={{
                      top: menuPosition.y,
                      left: menuPosition.x,
                    }}
                  >
                    {contactVariables.map((variable) => (
                      <MenuItem
                        key={variable}
                        onClick={() =>
                          handleVariableSelect(variable, "contact")
                        }
                      >
                        {variable}
                      </MenuItem>
                    ))}
                  </Menu>
                </MenuItem>
                <MenuItem onClick={() => setShowProfileVariables(true)}>
                  {t("message.profile")}
                  <Menu
                    open={showProfileVariables}
                    onClose={() => setShowProfileVariables(false)}
                    anchorReference="anchorPosition"
                    anchorPosition={{
                      top: menuPosition.y,
                      left: menuPosition.x,
                    }}
                  >
                    {profileVariables.map((variable) => (
                      <MenuItem
                        key={variable}
                        onClick={() =>
                          handleVariableSelect(variable, "profile")
                        }
                      >
                        {variable}
                      </MenuItem>
                    ))}
                  </Menu>
                </MenuItem>
                <MenuItem onClick={() => setShowKoubiVariables(true)}>
                  {t("message.koubi")}
                  <Menu
                    open={showKoubiVariables}
                    onClose={() => setShowKoubiVariables(false)}
                    anchorReference="anchorPosition"
                    anchorPosition={{
                      top: menuPosition.y,
                      left: menuPosition.x,
                    }}
                  >
                    {koubiVariables.map((variable) => (
                      <MenuItem
                        key={variable}
                        onClick={() => handleVariableSelect(variable, "koubi")}
                      >
                        {variable}
                      </MenuItem>
                    ))}
                  </Menu>
                </MenuItem>
              </Menu>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="file-select-label">
                  {t("template.file")}
                </InputLabel>
                <Select
                  labelId="file-select-label"
                  id="file-select"
                  value={formData.file_id || ""}
                  label={t("template.file")}
                  onChange={(e) => handleChange("file_id", e.target.value)}
                >
                  <MenuItem value="">{t("template.none")}</MenuItem>
                  {files.map((file) => (
                    <MenuItem key={file.id} value={file.id}>
                      {file.name}
                    </MenuItem>
                  ))}
                  <MenuItem onClick={handleNewFileClick}>
                    {t("template.newFile")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", mt: 4, gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    borderRadius: "8px",
                    fontWeight: "bold",
                    py: 1.5,
                    background: "linear-gradient(90deg, #7949E7 0%, #9A67ED 100%)",
                    color: "#fff",
                    boxShadow: "0 4px 14px 0 rgba(121, 73, 231, 0.39)",
                    "&:hover": { background: "linear-gradient(90deg, #6B53BB 0%, #7949E7 100%)" }
                  }}
                >
                  {t(
                    isEditing ? "template.saveChanges" : "template.addTemplate"
                  )}
                </Button>

                {isEditing && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDelete}
                    fullWidth
                    sx={{ borderRadius: "8px", fontWeight: "bold", borderWidth: "2px", "&:hover": { borderWidth: "2px" }, color: "#e2726e", borderColor: "#e2726e", py: 1.5 }}
                  >
                    {t("template.delete")}
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </form>

        <ConfirmationModal
          open={showDeleteConfirmation}
          onClose={handleDeleteCancellation}
          onConfirm={handleDeleteConfirmation}
          title={t("template.confirmDelete")}
          message={t("template.confirmDeleteMessage")}
        />
      </Box>
    </Modal>
  );
}
